import { RouteRecordRaw } from "vue-router"
import { useAcl } from "vue-simple-acl"
import { h, resolveComponent } from "vue"

import { Item } from "@/models/Item"
import { $t } from "@/plugins/i18n"
import { UnleashFlags } from "@/plugins/unleash"
import AccountRouterView from "@/router/AccountRouterView.vue"
import { parsingProjectIdAndItemNumber, splitAndCapitalize } from "@/utils/helpers"

export const pathSignUp = "/sign-up"

function page(path: string) {
  return () => import(/* webpackChunkName: '' */ `../views/${path}`).then(m => m.default || m)
}

const routers: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: to => ({ path: `/${to.params.accountId}` })
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/error"
  },
  {
    path: "/error",
    name: "Error",
    component: page("Error.vue"),
    meta: {
      layout: "ErrorLayout"
    }
  },
  {
    path: "/subscription",
    name: "Subscriptions",
    component: page("Create/CreateAccount/Subscription.vue"),
    meta: {
      layout: "EmptyLayout",
      createAccount: true
    }
  },
  {
    path: "/invite/:invitationId/wrong-invitee-error",
    name: "WrongInviteeError",
    component: page("WrongInviteeError.vue"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true
    }
  },
  {
    path: "/service-unavailable",
    name: "ServiceUnavailable",
    component: page("ServiceUnavailable.vue"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true
    }
  },
  {
    path: "/company/create/:priceId?",
    name: "CompanySetup",
    component: page("Create/CreateAccount"),
    meta: {
      layout: "EmptyLayout"
    }
  },
  {
    path: "/company/invite",
    name: "InviteUser",
    component: page("Create/CreateAccount/InviteUser.vue"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/verification-alert/:priceId?",
    name: "VerificationAlert",
    component: page("Verification/VerificationAlert"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true
    }
  },
  {
    path: "/email-verification",
    name: "VerificationEmail",
    component: page("Verification/VerificationEmail"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/storybook",
    name: "Storybook",
    component: page("Storybook"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true
    }
  },
  {
    path: "/subscription/:priceId",
    name: "Subscription",
    component: page("Subscription"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/redeem",
    name: "RedeemCode",
    component: page("RedeemCode"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/subscription-success/:sessionId",
    name: "SubscriptionSuccess",
    component: page("Subscription"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/subscription-cancel/:sessionId",
    name: "SubscriptionCancel",
    component: page("Subscription"),
    meta: {
      layout: "EmptyLayout",
      withoutAccount: true,
      createAccount: true
    }
  },
  {
    path: "/accounts",
    component: {
      render() {
        return h(resolveComponent("router-view"))
      }
    },
    children: [
      {
        path: "",
        name: "accounts",
        component: page("Accounts.vue"),
        meta: {
          adminPage: true,
          superAdminOnly: true,
          middleware: "adminGuard",
          browserTabConfig: {
            title: "Accounts"
          }
        }
      },
      {
        path: "/edit-account/:accountId",
        name: "edit-account",
        component: page("AdminPanel/AccountPage.vue"),
        meta: {
          aiSuggestionKey: "ACCOUNT_SETTINGS",
          breadcrumbConfigList: [
            {
              bcWait: "account.get",
              bcGetter: "GET_ACCOUNT",
              breadcrumbTitle: ({ value }) => (value?.name ? value.name : "Edit Account")
            }
          ],
          browserTabConfig: {
            getter: "GET_ACCOUNT",
            title: ({ value }) => (value?.name ? `Account ${value.name}` : ""),
            wait: "account.get"
          },
          adminPage: true,
          middleware: "adminGuard"
        }
      }
    ]
  },
  {
    path: "/settings",
    component: {
      render() {
        return h(resolveComponent("router-view"))
      }
    },
    meta: {
      superAdminOnly: true,
      middleware: "adminGuard"
    },
    children: [
      {
        path: "",
        name: "settings",
        component: page("Settings"),
        meta: {
          adminPage: true,
          browserTabConfig: {
            title: "Settings"
          }
        }
      },
      {
        path: "ai-providers",
        name: "ai-providers",
        component: page("Settings"),
        meta: {
          adminPage: true,
          browserTabConfig: {
            title: "Settings"
          }
        }
      },
      {
        path: "edit-template/:template",
        name: "edit-template",
        component: page("Emails/CreateTemplate"),
        meta: {
          adminPage: true,
          browserTabConfig: {
            getter: "GET_EMAIL_TEMPLATE_TYPE",
            title: ({ value }) =>
              value?.type ? `Email Template ${splitAndCapitalize(value.type)}` : ""
          }
        }
      }
    ]
  },
  {
    path: "/ai-activity-log/:accountId?",
    name: "ai-activity-log",
    component: page("AIActivityLog.vue"),
    meta: {
      aiSuggestionKey: "AI_ACTIVITY_LOG",
      adminPage: true,
      showAdminPanelTitle: true,
      hideBackButtonInFooter: true,
      middleware: "adminGuard",
      browserTabConfig: {
        title: "AI Activity Log"
      }
    }
  },
  {
    path: "/:accountId",
    component: AccountRouterView,
    children: [
      {
        path: "",
        redirect: { name: "projects" }
      },
      {
        path: "disabled",
        name: "DisabledAccount",
        component: page("DisabledAccount"),
        meta: {
          layout: "EmptyLayout",
          middleware: "disabledAccountMiddleware"
        }
      },
      {
        path: "subscription-error",
        name: "SubscriptionError",
        component: page("SubscriptionError.vue"),
        meta: {
          layout: "ErrorLayout"
        }
      },
      {
        path: "temporarily-disabled",
        name: "TemporarilyDisabledAccount",
        component: page("TemporarilyDisabledAccount"),
        meta: {
          layout: "EmptyLayout",
          withoutAccount: true,
          createAccount: true,
          disabledDarkMode: true,
          middleware: "disabledPaymentMiddleware"
        }
      },
      {
        path: "myWork",
        name: "myWork",
        meta: {
          aiSuggestionKey: "MY_WORK",
          breadcrumbConfigList: [
            {
              bcGetter: "GET_ITEM",
              breadcrumbTitle: ({ route }) => (!route.query?.i ? "My Work" : "")
            }
          ],
          browserTabConfig: {
            title: "My Work"
          },
          showMyWorkNavigation: true
        },
        component: page("MyWork.vue")
      },
      {
        path: "myWork/:projectId",
        name: "myWorkEditItem",
        meta: {
          aiSuggestionKey: "ITEM_DETAILS",
          breadcrumbConfigList: [
            {
              bcGetter: "GET_ITEM",
              breadcrumbTitle: ({ value }) => (value?.team ? value.team.name : "")
            },
            {
              bcGetter: "GET_ITEM",
              breadcrumbLink: ({ value, route }) => ({
                title: value.team ? Item.getBacklogNameByItem(value) : "",
                route: {
                  name: "backlog",
                  params: {
                    ...route.params,
                    teamId: value?.team?.urlName ?? ""
                  },
                  query: Item.getBacklogQueryByItem(value)
                }
              })
            },
            {
              bcGetter: "GET_ITEM",
              breadcrumbTitle: ({ value, route }) => {
                if (!route.query.i) return ""
                return value?.id ? "Edit Item" : "Create Item"
              }
            }
          ],
          browserTabConfig: {
            title: "My Work"
          }
        },
        component: page("MyWork.vue")
      },
      {
        path: "team-calendar",
        name: "team-calendar",
        meta: {
          aiSuggestionKey: "TEAM_CALENDAR",
          breadcrumbConfigList: [
            {
              breadcrumbTitle: "Calendar"
            }
          ],
          browserTabConfig: {
            title: "Calendar"
          }
        },
        component: page("TeamCalendar/index.vue")
      },
      {
        path: "timesheet",
        component: {
          render() {
            return h(resolveComponent("router-view"))
          }
        },
        children: [
          {
            path: ":timesheetId",
            name: "timesheet",
            meta: {
              updateComponentByRoute: true,
              breadcrumbConfigList: [
                {
                  breadcrumbTitle: "Timesheet"
                }
              ],
              browserTabConfig: {
                title: "Timesheet"
              },
              layout: "EmptyLayoutWithHeader",
              featureFlag: UnleashFlags.TIME_TRACKING
            },
            component: page("Timesheet/index.vue")
          },
          {
            path: "mytime",
            name: "myTimesheet",
            meta: {
              browserTabConfig: {
                title: "My Timesheet"
              },
              layout: "EmptyLayoutWithHeader",
              featureFlag: UnleashFlags.TIME_TRACKING
            },
            component: page("Timesheet/History.vue")
          },
          {
            path: "approval",
            name: "timesheetApproval",
            meta: {
              browserTabConfig: {
                title: "Timesheet Approval"
              },
              layout: "EmptyLayoutWithHeader",
              featureFlag: UnleashFlags.TIME_TRACKING,
              middleware: "timesheetMiddleware"
            },
            component: page("Timesheet/Review.vue")
          },
          {
            path: "summaries",
            name: "timesheetSummaries",
            meta: {
              browserTabConfig: {
                title: "Timesheet - Monthly Summary"
              },
              layout: "EmptyLayoutWithHeader",
              featureFlag: UnleashFlags.TIME_TRACKING,
              middleware: "timesheetMiddleware"
            },
            component: page("Timesheet/Summaries.vue")
          },
          {
            path: "summary/:projectId",
            name: "timesheetSummariesWeeks",
            meta: {
              browserTabConfig: {
                title: "Timesheet - Monthly Summary"
              },
              breadcrumbConfigList: [
                {
                  breadcrumbTitle: "Timesheet"
                },
                {
                  breadcrumbTitle: "Monthly Summary"
                }
              ],
              layout: "EmptyLayoutWithHeader",
              featureFlag: UnleashFlags.TIME_TRACKING
            },
            component: page("Timesheet/MonthlyReview.vue")
          }
        ]
      },
      {
        path: "roadmap/:projectId?",
        name: "roadmap",
        meta: {
          aiSuggestionKey: "ROADMAP",
          browserTabConfig: {
            getter: "FILTER_BY_PROJECT",
            title: ({ value }) => `${value?.name ?? ""} Roadmap`
          },
          breadcrumbConfigList: [
            {
              breadcrumbTitle: "Roadmap"
            }
          ]
        },
        component: page("Roadmap.vue")
      },
      {
        path: "ideas/:projectId?",
        name: "ideas",
        meta: {
          aiSuggestionKey: "IDEAS",
          browserTabConfig: {
            getter: "GET_PROJECT",
            title: ({ value }) => (value?.name ? `${value.name} Ideas` : "")
          },
          breadcrumbConfigList: [
            {
              breadcrumbLink: ({ route }) => {
                if (!route.query.idea) return undefined
                const query = { ...route.query }
                delete query.idea
                return {
                  title: "Ideas",
                  route: { name: "ideas", params: { projectId: route.params.projectId }, query }
                }
              },
              breadcrumbTitle: "Ideas"
            },
            {
              bcGetter: "GET_IDEA",
              breadcrumbTitle: ({ route }) => (route.query.idea ? "Edit Idea" : "")
            }
          ]
        },
        component: page("Ideas/IdeasList.vue")
      },
      {
        path: "past-goals/:projectId?",
        name: "past-goals",
        meta: {
          aiSuggestionKey: "PAST_GOALS",
          browserTabConfig: {
            getter: "FILTER_BY_PROJECT",
            title: ({ value }) => `${value?.name ?? ""} Past Goals`
          }
        },
        component: page("Roadmap.vue")
      },
      {
        path: "user/:userId?",
        name: "edit-user",
        component: page("AdminPanel/UserPage.vue"),
        meta: {
          aiSuggestionKey: "USER_PROFILE",
          breadcrumbConfigList: [
            {
              bcGetter: "GET_USER_CURRENT_PROFILE",
              breadcrumbLink: ({ value, route }) => {
                const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                if (!route.query.myAccount || !value?.accounts || !isAdmin) return null
                const urlName = value.accounts.find(
                  account => account.id === value.accountId
                )?.urlName
                if (!urlName) return null
                return {
                  title: "My Account",
                  route: {
                    name: "edit-account",
                    params: {
                      accountId: urlName
                    }
                  }
                }
              }
            },
            {
              bcGetter: "GET_USER_CURRENT_PROFILE",
              breadcrumbLink: ({ value, route }) => {
                const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                if (!route.query.myAccount || !value?.accounts || !isAdmin) return null
                const urlName = value.accounts.find(
                  account => account.id === value.accountId
                )?.urlName
                if (!urlName) return null
                return {
                  title: "Users",
                  route: {
                    name: "edit-account",
                    params: {
                      accountId: urlName
                    },
                    query: {
                      tab: "users"
                    }
                  }
                }
              }
            },
            {
              bcGetter: "USER_BY_ID",
              breadcrumbTitle: ({ value }) => value?.fullName ?? "My Profile"
            }
          ],
          browserTabConfig: {
            getter: "USER_BY_ID",
            title: ({ value, route }) =>
              value && route.params.userId === value.id ? `${value.fullName} Profile` : "My Profile"
          },
          updateComponentByRoute: true
        }
      },

      {
        path: "team/:teamId?",
        name: "edit-team",
        component: page("Create/CreateTeam/CreateTeam.vue"),
        meta: {
          aiSuggestionKey: "TEAM_SETTINGS",
          hideTeamSection: true,
          title: "My Account",
          breadcrumbConfigList: [
            {
              bcGetter: "GET_USER_CURRENT_PROFILE",
              breadcrumbLink: ({ value }) => {
                const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                if (!value?.accounts || !isAdmin) return null
                const urlName = value.accounts.find(
                  account => account.id === value.accountId
                )?.urlName
                if (!urlName) return null
                return {
                  title: "My Account",
                  route: {
                    name: "edit-account",
                    params: {
                      accountId: urlName
                    }
                  }
                }
              }
            },
            {
              bcGetter: "GET_USER_CURRENT_PROFILE",
              breadcrumbLink: ({ value }) => {
                const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                if (!value?.accounts || !isAdmin) return null
                const urlName = value.accounts.find(
                  account => account.id === value.accountId
                )?.urlName
                if (!urlName) return null
                return {
                  title: "Teams",
                  route: {
                    name: "edit-account",
                    params: {
                      accountId: urlName
                    },
                    query: {
                      tab: "teams"
                    }
                  }
                }
              }
            },
            {
              bcGetter: "GET_TEAM",
              breadcrumbTitle: ({ value }) => value.name || ""
            }
          ]
        }
      },
      {
        path: "projects",
        component: {
          render() {
            return h(resolveComponent("router-view"))
          }
        },
        children: [
          {
            path: "",
            name: "projects",
            meta: {
              aiSuggestionKey: "PROJECT_LIST",
              hideBackButtonInFooter: true,
              browserTabConfig: {
                title: "Project Simple"
              },
              updateComponentByRoute: true
            },
            component: page("Projects/index.vue")
          },
          {
            path: ":projectId",
            name: "project",
            component: page("Projects/index.vue"),
            meta: {
              aiSuggestionKey: "PROJECT_OVERVIEW",
              hideProjectLabel: true,
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => value.name ?? ""
              },
              updateComponentByRoute: true,
              breadcrumbConfigList: [
                {
                  bcGetter: "GET_USER_CURRENT_PROFILE",
                  breadcrumbLink: ({ value, route }) => {
                    const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                    if (!route.query.myAccount || !value?.accounts || !isAdmin) return null
                    const urlName = value.accounts.find(
                      account => account.id === value.accountId
                    )?.urlName
                    if (!urlName) return null
                    return {
                      title: "My Account",
                      route: {
                        name: "edit-account",
                        params: {
                          accountId: urlName
                        }
                      }
                    }
                  }
                },
                {
                  bcGetter: "GET_USER_CURRENT_PROFILE",
                  breadcrumbLink: ({ value, route }) => {
                    const isAdmin = useAcl().can.any(["admin", "superAdmin"])
                    if (!route.query.myAccount || !value?.accounts || !isAdmin) return null
                    const urlName = value.accounts.find(
                      account => account.id === value.accountId
                    )?.urlName
                    if (!urlName) return null
                    return {
                      title: "Projects",
                      route: {
                        name: "edit-account",
                        params: {
                          accountId: urlName
                        },
                        query: {
                          tab: "projects"
                        }
                      }
                    }
                  }
                },
                {
                  bcGetter: "GET_PROJECT",
                  breadcrumbTitle: ({ value }) => (value ? value.name : ""),
                  wait: "project.get"
                }
              ]
            }
          }
        ]
      },
      {
        path: "create-project",
        name: "create-project",
        component: page("Create/CreateProjectWizard"),
        meta: {
          middleware: "adminGuard",
          browserTabConfig: {
            title: "New Project"
          },
          layout: "EmptyLayoutWithHeader"
        }
      },
      {
        path: ":projectId-:itemId",
        name: "Item",
        component: page("Create/CreateItem/CreateItem.vue"),
        beforeEnter: (to, from, next) => {
          const initialParams = to.params.projectId + "-" + to.params.itemId

          const result = parsingProjectIdAndItemNumber(initialParams)

          to.params.projectId = result.projectId
          to.params.itemId = result.itemId
          next()
        },
        meta: {
          aiSuggestionKey: "ITEM_DETAILS",
          breadcrumbConfigList: [
            {
              bcGetter: "GET_ITEM",
              bcWait: "checkUserInItem",
              breadcrumbLink: ({ value, route }) => {
                if (!value?.id) return null

                const isSprintCompleted =
                  value.sprint?.status === "COMPLETED" || value.sprint?.status === "HALTED"
                const params = {
                  ...route.params,
                  teamId: value?.team?.urlName ?? ""
                }

                if (isSprintCompleted) {
                  params.sprintId = value.sprint?.sequenceNumber
                }
                let name = value.epic ? "epics" : "product-backlog"
                let title = Item.getBacklogNameByItem(value)
                let query = {}
                if (value.team) {
                  name = isSprintCompleted ? "SprintReview" : "backlog"
                  query = isSprintCompleted ? {} : Item.getBacklogQueryByItem(value)
                }
                if (!value.team) {
                  title = value.epic ? "Epics" : "Product Backlog"
                }

                return {
                  title,
                  route: {
                    name,
                    params,
                    query,
                    // Back from the review should return to the backlog, skipping these details.
                    replace: name === "SprintReview"
                  }
                }
              }
            },
            {
              bcGetter: "GET_ITEM",
              bcWait: "checkUserInItem",
              breadcrumbLink: ({ value, route }) =>
                !value.team && value?.epic
                  ? {
                      title: `Epic #${value.epic.sequenceNumber}`,
                      route: {
                        name: "epic",
                        params: {
                          ...route.params,
                          epicId: value.epic.sequenceNumber ?? ""
                        }
                      }
                    }
                  : null
            },
            {
              bcGetter: "GET_ITEM",
              bcWait: "checkUserInItem",
              breadcrumbTitle: ({ value }) => (value?.id ? "Edit Item" : "Create Item")
            }
          ],
          breadcrumbSelectedTeam: {
            bcGetter: "GET_ITEM",
            selectedTeam: ({ value }) => value?.team
          },
          browserTabConfig: {
            getter: "GET_ITEM",
            title: ({ value }) => (value?.id ? `#${value.sequenceNumber} ${value.title}` : "")
          },
          canSelectTeam: true,
          updateComponentByRoute: true,
          navigateToByTeamBreadcrumbs: "backlog",
          middleware: "checkUserInTeamForItem"
        }
      },
      {
        path: ":projectId",
        component: {
          render() {
            return h(resolveComponent("router-view"))
          }
        },
        children: [
          {
            path: "edit",
            name: "edit-project",
            component: page("Create/CreateProject"),
            meta: {
              aiSuggestionKey: "PROJECT_SETTINGS",
              editProject: true,
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Settings` : "")
              },
              breadcrumbConfigList: [
                {
                  bcGetter: "SHOW_ADVANCE_SETTINGS",
                  breadcrumbTitle: ({ value }) => (value ? "Advanced Settings" : "Project Settings")
                }
              ],
              hideBackButtonInFooter: false
            }
          },
          {
            path: "project-wizard",
            name: "project-wizard",
            component: page("Create/CreateProjectWizard"),
            meta: {
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Settings` : "")
              },
              layout: "EmptyLayoutWithHeader"
            }
          },
          {
            path: "create-team",
            name: "create-team",
            component: page("Create/CreateTeamWizard"),
            meta: {
              middleware: "adminGuard",
              browserTabConfig: {
                title: "New Team"
              },
              layout: "EmptyLayoutWithHeader",
              hideTeamSection: true
            }
          },
          {
            path: "epics",
            name: "epics",
            meta: {
              aiSuggestionKey: "EPICS",
              breadcrumbConfigList: [
                {
                  breadcrumbTitle: "Epics"
                }
              ],
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Epics` : ""),
                wait: "teamsAndProject.get"
              }
            },
            component: page("Epics.vue")
          },
          {
            path: "epic/:epicId",
            name: "epic",
            component: page("Create/CreateEpic"),
            meta: {
              aiSuggestionKey: "EPIC_DETAILS",
              createStoryInBacklog: true,
              breadcrumbConfigList: [
                {
                  breadcrumbLink: () => ({
                    title: "Epics",
                    route: {
                      name: "epics"
                    }
                  })
                },
                {
                  bcGetter: "GET_EPIC",
                  bcWait: "epic.get",
                  breadcrumbLink: ({ value, route }) => {
                    if (!route.query.epicDetails || !value?.sequenceNumber) return null

                    const query = { ...route.query }
                    delete query.epicDetails

                    return {
                      title: `Epic #${value.sequenceNumber}`,
                      route: {
                        name: "epic",
                        params: { ...route.params, epicId: value.sequenceNumber },
                        query
                      }
                    }
                  },
                  breadcrumbTitle: ({ value, route }) => {
                    if (route.query.i || route.query.epicDetails) return ""
                    return value?.sequenceNumber ? `Epic #${value.sequenceNumber}` : ""
                  }
                },
                {
                  bcGetter: "GET_ITEM",
                  breadcrumbLink: ({ value, route }) =>
                    value?.epic && route.query.i
                      ? {
                          title: `Epic #${value.epic.sequenceNumber}`,
                          route: {
                            name: "epic",
                            params: {
                              ...route.params,
                              epicId: value.epic.sequenceNumber ?? ""
                            }
                          }
                        }
                      : null
                },
                {
                  bcGetter: "GET_ITEM",
                  breadcrumbTitle: ({ value, route }) => {
                    if (!route.query.i) return ""
                    return value?.id ? "Edit Item" : "Create Item"
                  },
                  bcWait: "get.item"
                },
                {
                  bcGetter: "GET_EPIC",
                  bcWait: "epic.get",
                  breadcrumbTitle: ({ route }) => {
                    if (!route.query.epicDetails) return ""
                    return "Epic Details"
                  }
                }
              ],
              browserTabConfig: {
                getter: "GET_EPIC",
                secondaryGetter: "GET_ITEM",
                title: ({ value, route, secondaryValue }) => {
                  if (route.query.i === "create") return "New Item"
                  if (route.query.i && secondaryValue?.id) {
                    return `#${secondaryValue.sequenceNumber} ${secondaryValue.title}`
                  }
                  return value?.name && value.sequenceNumber
                    ? `E-${value.sequenceNumber} ${value.name}`
                    : ""
                },
                wait: "epic.get"
              }
            }
          },
          {
            path: "edit-sprint/:teamId/:sprintId",
            name: "edit-sprint",
            meta: {
              aiSuggestionKey: "SPRINT_SETUP",
              cacheData: true,
              updateComponentByRoute: true,
              middleware: "createSprintMiddleware"
            },
            component: page("Create/CreateSprint/index.vue")
          },
          {
            path: "create-sprint",
            name: "create-sprint",
            meta: {
              aiSuggestionKey: "SPRINT_SETUP",
              updateComponentByRoute: true,
              browserTabConfig: {
                title: "New Sprint"
              },
              middleware: "createSprintMiddleware"
            },
            component: page("Create/CreateSprint/index.vue")
          },
          {
            path: "sprints",
            name: "sprints",
            meta: {
              aiSuggestionKey: "SPRINT_TILES",
              breadcrumbConfigList: [
                {
                  breadcrumbTitle: "Sprints"
                }
              ],
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Sprints` : ""),
                wait: "teamsAndProject.get"
              },
              middleware: "sprintsMiddleware"
            },
            component: page("Sprints.vue")
          },
          {
            path: "milestones/:milestoneId?",
            name: "Milestones",
            meta: {
              aiSuggestionKey: "MILESTONES",
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Milestones` : ""),
                wait: "teamsAndProject.get"
              },
              breadcrumbConfigList: [
                {
                  // A Goal opened from a Roadmap tile links back to the Roadmap.
                  breadcrumbLink: ({ route }) =>
                    route.query.goal
                      ? {
                          title: "Roadmap",
                          route: { name: "roadmap", params: { projectId: route.params.projectId } }
                        }
                      : undefined,
                  breadcrumbTitle: "Milestones"
                }
              ]
            },
            component: page("Milestone.vue")
          },
          {
            path: "allSprints",
            name: "allSprints",
            meta: {
              aiSuggestionKey: "ALL_SPRINTS",
              breadcrumbConfigList: [
                {
                  breadcrumbTitle: "All Sprints"
                }
              ],
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} All Sprints` : ""),
                wait: "teamsAndProject.get"
              }
            },
            component: page("AllSprints.vue")
          },
          {
            path: "retros",
            name: "retros",
            meta: {
              aiSuggestionKey: "RETROSPECTIVES",
              browserTabConfig: {
                getter: "GET_PROJECT",
                title: ({ value }) => (value?.name ? `${value.name} Retrospectives` : ""),
                wait: "teamsAndProject.get"
              }
            },
            component: page("Retrospective/AllRetrospectives.vue")
          },
          {
            path: "product-backlog",
            name: "product-backlog",
            meta: {
              aiSuggestionKey: "PRODUCT_BACKLOG",
              createStoryInBacklog: true,
              browserTabConfig: {
                secondaryGetter: "GET_ITEM",
                title: ({ route, secondaryValue }) => {
                  if (route.query.i === "create") return "New Item"
                  if (route.query.i && secondaryValue?.id) {
                    return `#${secondaryValue.sequenceNumber} ${secondaryValue.title}`
                  }
                  return "Product Backlog"
                }
              },
              breadcrumbConfigList: [
                {
                  bcGetter: "GET_ITEM",
                  breadcrumbTitle: ({ route }) => (!route.query.i ? "Product Backlog" : null)
                },
                {
                  bcGetter: "GET_ITEM",
                  breadcrumbLink: ({ route }) =>
                    route.query.i
                      ? {
                          title: "Product Backlog",
                          route: {
                            name: "product-backlog"
                          }
                        }
                      : null
                },
                {
                  bcGetter: "GET_ITEM",
                  breadcrumbTitle: ({ value, route }) => {
                    if (!route.query.i) return ""
                    return value?.id ? "Edit Item" : "Create Item"
                  }
                }
              ]
            },
            component: page("ProductBacklog/index.vue")
          },
          {
            path: "team-calendar",
            redirect: to => ({
              name: "team-calendar",
              params: {
                accountId: to.params.accountId
              }
            })
          },
          {
            path: ":teamId?",
            component: {
              render() {
                return h(resolveComponent("router-view"))
              }
            },
            children: [
              {
                path: "items/create",
                name: "CreateItem",
                component: page("Create/CreateItem/CreateItem.vue"),
                meta: {
                  aiSuggestionKey: "ITEM_DETAILS",
                  hideTeamSection: true,
                  breadcrumbConfigList: [
                    {
                      bcGetter: "GET_ITEM",
                      breadcrumbTitle: ({ value }) => (value.team ? value.team.name : "")
                    },
                    {
                      bcGetter: "GET_ITEM",
                      breadcrumbLink: ({ value, route }) => {
                        if (route.params.teamId && !value.team) return null

                        let name = "backlog"
                        let title = Item.getBacklogNameByItem(value)
                        if (!value.team) {
                          name = value.epic ? "epics" : "product-backlog"
                          title = value.epic ? "Epics" : "Product Backlog"
                        }
                        const params = value.team ? { teamId: value.team.urlName } : {}
                        const query = value.team ? Item.getBacklogQueryByItem(value) : {}

                        return {
                          title,
                          route: {
                            name,
                            params,
                            query
                          }
                        }
                      }
                    },
                    {
                      bcGetter: "GET_ITEM",
                      breadcrumbLink: ({ value, route }) =>
                        value.epic && !value.team
                          ? {
                              title: `Epic #${value.epic.sequenceNumber}`,
                              route: {
                                name: "epic",
                                params: {
                                  ...route.params,
                                  epicId: value.epic.sequenceNumber ?? ""
                                }
                              }
                            }
                          : null
                    },
                    {
                      bcGetter: "GET_ITEM",
                      bcWait: "checkUserInItem",
                      breadcrumbTitle: ({ value }) => (value?.id ? "Edit Item" : "Create Item")
                    }
                  ],
                  updateComponentByRoute: true,
                  browserTabConfig: {
                    title: "New Item"
                  },
                  middleware: "checkUserInTeamForItem"
                }
              },
              {
                path: "backlog",
                name: "backlog",
                meta: {
                  aiSuggestionKey: "TEAM_BACKLOG",
                  createStoryInBacklog: true,
                  breadcrumbConfigList: [
                    {
                      bcGetter: "GET_MAIN_BACKLOG_SETUP",
                      breadcrumbTitle: ({ value, route }) => {
                        if (route.query?.i || !value) return ""

                        const backlogTitle = $t(`backlog.titles.${value.storeBacklogId}`)

                        return value?.sprintInfo?.sequenceNumber
                          ? backlogTitle + ` #${value.sprintInfo.sequenceNumber}`
                          : backlogTitle
                      },
                      bcWait: "backlogLoading"
                    },
                    {
                      bcGetter: "GET_ITEM",
                      breadcrumbLink: ({ value, route }) => {
                        if (!value || !route.query.i) {
                          return
                        }
                        /** A closed sprint has no backlog tab; its items link to the review instead. */
                        if (
                          value.team &&
                          (value.sprint?.status === "COMPLETED" ||
                            value.sprint?.status === "HALTED")
                        ) {
                          return {
                            title: Item.getBacklogNameByItem(value),
                            route: {
                              name: "SprintReview",
                              replace: true,
                              params: {
                                ...route.params,
                                teamId: value.team.urlName,
                                sprintId: value.sprint.sequenceNumber
                              }
                            }
                          }
                        }
                        const params = { ...route.params }
                        return {
                          title: Item.getBacklogNameByItem(value),
                          route: {
                            ...route,
                            query: Item.getBacklogQueryByItem(value),
                            params: value?.team
                              ? {
                                  ...params,
                                  teamId: value.team.urlName
                                }
                              : params
                          }
                        }
                      },
                      bcWait: "get.item"
                    },
                    {
                      bcGetter: "GET_ITEM",
                      breadcrumbTitle: ({ value, route }) =>
                        route.query.i === "create" ? "Create Item" : value?.id ? "Edit Item" : ""
                    }
                  ],
                  browserTabConfig: {
                    getter: "GET_SESSION_TEAM",
                    secondaryGetter: "GET_ITEM",
                    title: ({ value, route, secondaryValue }) => {
                      if (value?.name && !route.query.i) return `${value.name} Backlog`
                      if (route.query.i === "create") return "New Item"
                      return secondaryValue?.id
                        ? `#${secondaryValue.sequenceNumber} ${secondaryValue.title}`
                        : ""
                    },
                    wait: "get.item"
                  },
                  breadcrumbSelectedTeam: {
                    bcGetter: "GET_SESSION_TEAM",
                    breadcrumbTitle: ({ value }) => value?.name ?? ""
                  },
                  canSelectTeam: true,
                  updateComponentByRoute: true,
                  middleware: "backlogMiddleware",
                  breadcrumbSelectedProject: {
                    bcGetter: "GET_ITEM",
                    selectedProject: ({ value }) => value?.project
                  }
                },
                component: page("Home.vue")
              }
            ]
          },
          {
            path: ":teamId",
            component: {
              render() {
                return h(resolveComponent("router-view"))
              }
            },
            children: [
              {
                path: "team-calendar",
                redirect: to => ({
                  name: "team-calendar",
                  params: {
                    accountId: to.params.accountId
                  }
                })
              },
              {
                path: "storypoker",
                name: "storypoker",
                meta: {
                  aiSuggestionKey: "STORY_POKER",
                  breadcrumbConfigList: [
                    {
                      breadcrumbTitle: "Story Poker"
                    }
                  ],
                  browserTabConfig: {
                    getter: "FILTER_BY_TEAM",
                    title: ({ value }) => (value?.name ? `${value.name} Story Poker` : "")
                  },
                  canSelectTeam: true
                },
                component: page("StoryPoker")
              },
              {
                path: "sprint",
                component: {
                  render() {
                    return h(resolveComponent("router-view"))
                  }
                },
                children: [
                  {
                    path: ":sprintId",
                    component: {
                      render() {
                        return h(resolveComponent("router-view"))
                      }
                    },
                    children: [
                      {
                        path: "",
                        name: "sprint",
                        component: page("SprintOverview"),
                        meta: {
                          aiSuggestionKey: "SPRINT_OVERVIEW",
                          updateComponentByRoute: true,
                          browserTabConfig: {
                            getter: "SPRINT_WITH_STATS",
                            title: ({ value }) => {
                              if (!value) return ""
                              const { sprint } = value
                              return `${sprint.team.name} Sprint #${sprint.sequenceNumber} Overview`
                            },
                            wait: "loading"
                          },
                          middleware: "sprintOverviewMiddleware"
                        }
                      },
                      {
                        path: "analytics",
                        name: "sprint-analytics",
                        component: page("SprintAnalytics/index.vue"),
                        meta: {
                          aiSuggestionKey: "SPRINT_ANALYTICS",
                          updateComponentByRoute: true,
                          hideSprintBreadcrumb: true,
                          browserTabConfig: {
                            getter: "SPRINT_ANALYTICS",
                            title: ({ value }) =>
                              value?.sprint
                                ? `Sprint #${value.sprint.sequenceNumber} Analytics`
                                : "Project Simple"
                          },
                          breadcrumbConfigList: [
                            {
                              bcGetter: "SPRINT_ANALYTICS",
                              breadcrumbLink: ({ value, route }) => {
                                if (!value?.sprint) {
                                  return
                                }
                                return {
                                  title: `Sprint #${value.sprint.sequenceNumber}`,
                                  route: {
                                    params: { ...route.params },
                                    name: "sprint"
                                  }
                                }
                              },
                              bcWait: "get.sprintAnalytics"
                            },
                            {
                              breadcrumbTitle: "Sprint Analytics"
                            }
                          ]
                        }
                      },
                      {
                        path: "review",
                        name: "SprintReview",
                        meta: {
                          aiSuggestionKey: "AI_INSIGHTS_ON_SPRINT_REVIEW",
                          updateComponentByRoute: true,
                          breadcrumbConfigList: [
                            {
                              breadcrumbTitle: "Sprint Review"
                            }
                          ],
                          browserTabConfig: {
                            getter: "GET_SPRINT_REVIEW",
                            title: ({ value }) => {
                              if (!value) return ""
                              const { sprint } = value
                              return `${sprint.team.name} Sprint #${sprint.sequenceNumber} Review`
                            },
                            wait: "loading"
                          },
                          middleware: "sprintReviewMiddleware"
                        },
                        component: page("SprintReview.vue")
                      },

                      {
                        path: "add-retro-notes",
                        name: "AddRetroNotes",
                        meta: {
                          aiSuggestionKey: "RETRO_NOTES",
                          breadcrumbConfigList: [
                            {
                              breadcrumbTitle: "Add Retro Notes"
                            }
                          ],
                          browserTabConfig: {
                            getter: "GET_RETROSPECTIVE",
                            title: ({ value }) => {
                              if (!value?.id) return ""
                              const { sprint } = value
                              return `${sprint.team.name} Sprint #${sprint.sequenceNumber} Retrospective`
                            },
                            wait: "loading"
                          },
                          middleware: "sprintRetroNotesMiddleware"
                        },
                        component: page("Retrospective/RetroNotes.vue")
                      },
                      {
                        path: "retrospective-vote",
                        name: "retrospective-vote",
                        component: page("Retrospective/RetrospectiveVote.vue"),
                        meta: {
                          browserTabConfig: {
                            getter: "GET_RETROSPECTIVE",
                            title: ({ value }) => {
                              if (!value?.id) return ""
                              const { sprint } = value
                              return `${sprint.team.name} Sprint #${sprint.sequenceNumber} Retrospective`
                            },
                            wait: "loading"
                          },
                          middleware: "sprintRetrospectiveVoteMiddleware"
                        }
                      },
                      {
                        path: "retrospective",
                        name: "retrospective",
                        component: page("Retrospective/RetrospectiveBoard.vue"),
                        meta: {
                          aiSuggestionKey: "AI_RETROSPECTIVE_INSIGHTS",
                          browserTabConfig: {
                            getter: "GET_RETROSPECTIVE",
                            title: ({ value }) => {
                              if (!value?.id) return ""
                              const { sprint } = value
                              return `${sprint.team.name} Sprint #${sprint.sequenceNumber} Retrospective`
                            },
                            wait: "loading"
                          },
                          middleware: "sprintRetrospectiveMiddleware"
                        }
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]
export default routers
